import React, { FC, useState, useEffect, ReactNode } from 'react'
import { Space, Table, Typography, Modal, notification, Form, InputNumber, Button } from 'antd'
import { CloseCircleOutlined, CheckCircleOutlined, FormOutlined, DeleteOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import moment from 'moment'
import NoSpaceInput from '@/components/noSpaceInput'
import { addBU, delBU, getBUList, modifyBU } from '@/services/progress.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { IBu } from '@/store/models/auth.model'
import { getUuid } from '@/utils/util'
import { IAppState } from '@/store/reducers'
import { useSelector } from 'react-redux'
import Nav from '@/components/nav'
// import _ from 'lodash'

type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>
interface IColProperty {
    title?: ReactNode | string
    dataIndex?: string
    editable?: boolean
    ellipsis?: { showTitle: boolean },
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    render?: (_: any, record: any) => ReactNode
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    onCell?: (record: IBu) => any
}
interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
    editing: boolean
    dataIndex: string
    inputType: 'number' | 'text'
    title: string
    record: IBu
    index: number
    children: ReactNode
}

const BuAndCtListMana: FC = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const locationObj = useLocation()
    const { t, i18n } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [winH, setWinh] = useState(0)
    const [tableData, setTableData] = useState<IBu[]>([])
    const [editingKey, setEditingKey] = useState('')
    const [form] = Form.useForm()
    const userInfo = useSelector((state: IAppState) => state.auth.userInfo)
    const { Text } = Typography

    const isEditing = (record: IBu) => ('' + record.id) === editingKey

    const popMsg = (msg: string) => {
        const args = {
            message: msg,
            description: '',
            icon: <CheckCircleOutlined style={{ color: '#08993f' }} />,
            duration: 3,
        }
        notification.open(args)
    }
    
    const EditableCell: React.FC<EditableCellProps> = ({
        editing,
        dataIndex,
        inputType,
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        title,
        children,
        ...restProps
    }) => {
        const inputNode = inputType === 'number' ? <InputNumber /> : <NoSpaceInput />
        // console.log('title: ', Object.prototype.toString.call(title))
        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex}
                        style={{ margin: 0 }}
                        rules={[
                            {
                                required: true,
                                message: t('Please Enter') + (i18n.language !== 'zh' ? ' ' : '') + t('content'),
                            },
                        ]}
                    >
                        {inputNode}
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        )
    }
    
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const viewButton = (_: any, record: any) => {
        const editable = isEditing(record)
        return !editable ? (
            <Space size={20}>
                <Text className={`${editingKey === '' ? 'txt_blue hand' : ''}`} title={t('edit')} onClick={() => {
                    if (editingKey === '') {
                        setEditingKey('' + record.id)
                        edit(record)
                    }
                }}><FormOutlined style={{ fontSize: 16 }} /></Text>
                <Text className={`${editingKey === '' ? 'txt_red hand' : ''}`} title={t('delete')} onClick={() => {
                    if (editingKey === '') {
                        delBUFun(record.id)
                    }
                }}><DeleteOutlined style={{ fontSize: 16 }} /></Text>
            </Space>
        ) : (
            <Space size={20}>
                <Button type="text" htmlType="submit" className='btn_txt'>
                    <Text className='txt_blue hand' title={t('save')}><CheckCircleOutlined style={{ fontSize: 16 }} /></Text>
                </Button>
                <Text className='txt_red hand' title={t('cancel')} onClick={cancel}><CloseCircleOutlined style={{ fontSize: 16 }} /></Text>
            </Space>
        )
    }
    const columns: (ColumnTypes[number] & IColProperty)[] = [
        {
            title: () => <span>Div</span>,
            dataIndex: 'divName',
            key: 'divName',
            editable: true,
            width: 250
        },
        {
            title: () => <span>BU</span>,
            dataIndex: 'bu',
            key: 'bu',
            editable: true,
            width: 150
        },
        {
            title: () => <span>{t('creator')}</span>,
            dataIndex: 'creator',
            key: 'creator',
            width: 150,
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('createTime')}</span>,
            dataIndex: 'createTime',
            key: 'createTime',
            width: 150,
            ellipsis: { showTitle: true },
            render: (value: Date) => {
                return moment(value).format('YYYY-MM-DD')
            }
        },
        {
            title: () => <span>{t('updator')}</span>,
            dataIndex: 'updator',
            key: 'updator',
            width: 150,
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('modifyTime')}</span>,
            dataIndex: 'modifyTime',
            key: 'modifyTime',
            width: 150,
            ellipsis: { showTitle: true },
            render: (value: Date) => {
                return moment(value).format('YYYY-MM-DD')
            }
        },
        {
            title: () => <span>{t('Action')}</span>,
            dataIndex: 'operation',
            key: 'operation',
            width: 150,
            fixed: 'right',
            render: viewButton
        },
    ]

    const mergedColumns = columns.map((col): IColProperty => {
        if (!col.editable) {
            return col
        }
        return {
            ...col,
            onCell: (record: IBu) => ({
                record,
                inputType: 'text',
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        }
    })

    const addBuFun = () => {
        const key = getUuid(8)
        const newItem: IBu = {
            id: key,
            creator:userInfo?.nickName,
            divName: '',
            bu: ''
        }
        setTableData([newItem, ...tableData])
        edit(newItem)
    }
    const edit = (record: Partial<IBu>) => {
        form.setFieldsValue({ ...record })
        setEditingKey('' + record.id)
    }
    const cancel = () => {
        setEditingKey('')
        const arr = tableData.filter(item => ('' + item.id)?.length !== 8)
        setTableData(arr)
    }
    const save = async (record: any) => {
        try {
            // const newData = _.cloneDeep(tableData)
            // const findIndex = newData.findIndex(item => ('' + item.id) === ('' + record.id))
            setLoading(true)
            const row = await form.validateFields()
            if (record.id.length === 8) {
                const resId = await addBU(record)
                record.id = resId
            } else {
                await modifyBU({
                    ...row,
                    id: '' + record.id
                })
            }
            getList()
            setEditingKey('')
            // console.log('findIndex: ', findIndex)
            // if (findIndex !== -1) {
            //     newData.splice(findIndex, 1, {
            //         ...record,
            //         bu: row.bu,
            //         divName: row.divName
            //     })
            //     setTableData(newData)
            // }
            popMsg(t('save') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
        } catch (error: any) {
            setLoading(false)
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }
    const delBUFun = async (id: number) => {
        Modal.confirm({
            title: t('confirmDel'),
            centered: true,
            okText: t('confirm'),
            cancelText: t('cancel'),
            async onOk () {
                try {
                    await delBU('' + id)
                    popMsg(t('delete') + (i18n.language !== 'zh' ? ' ' : '') + t('success'))
                    setEditingKey('')
                    getList()
                } catch (error: any) {
                    const str = getErrorStr(error)
                    dispatch(requestFail(str))
                }
            }
        })
    }

    const onFinish = (values: IBu) => {
        save({
            ...values,
            id: editingKey
        })
    }

    const getList = async () => {
        try {
            setLoading(true)
            const res = await getBUList()
            setTableData(res)
            setLoading(false)
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
    }

    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const [routes, setRoutes] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        {
            path: '/buAndCtListMana',
            breadcrumbName: t('buAndCtList') + (i18n.language !== 'zh' ? ' ' : '') + t('manage'),
        },
    ])
    useEffect(() => {
        getList()

        const h = 285
        setWinh(document.body.clientHeight - h)
        window.onresize = () => {
            setWinh(document.body.clientHeight - h)
        }
        return () => {
            window.onresize = null
        }
    }, [])
    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                dispatch={dispatch}
            />
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Button type='primary'
                    style={{ margin: '0 25px' }}
                    onClick={addBuFun}
                    disabled={editingKey !== ''}
                >{t('add') + (i18n.language !== 'zh' ? ' ' : '') + t('buAndCtList')}</Button>
                <Form form={form} onFinish={onFinish}>
                    <Table bordered
                        className='index_ls'
                        components={{
                            body: {
                                cell: EditableCell,
                            },
                        }}
                        dataSource={tableData}
                        loading={loading}
                        columns={mergedColumns}
                        rowKey="id"
                        scroll={{ x: 'max-content', y: winH }}
                    />
                </Form>
            </Space>
        </Space>
    )
}

export default BuAndCtListMana